/**
 * Portal URL helpers for cross-app auth redirects.
 *
 * Apps that share Supabase SSR cookies with Agency OS must redirect
 * unauthenticated requests to the portal's login page. This module centralizes
 * how the portal URL is resolved from env and how the login redirect URL is
 * constructed, so that all consumer apps behave identically.
 */

type EnvLike = Record<string, string | undefined>;
type HeadersLike = {
  get(name: string): string | null | undefined;
};

/**
 * Resolve the portal URL from env.
 *
 * Reads NEXT_PUBLIC_PORTAL_URL first (client-safe) then PORTAL_URL
 * (server-only). Returns null when neither is set, rather than throwing --
 * consumers choose how to handle a missing portal URL (soft-fail fallback
 * to the request origin is the preferred path per doc 486 lane).
 */
export function resolvePortalUrl(env: EnvLike = process.env): string | null {
  const candidate =
    env.NEXT_PUBLIC_PORTAL_URL?.trim() || env.PORTAL_URL?.trim() || '';
  return candidate.length > 0 ? candidate : null;
}

export interface BuildLoginRedirectInput {
  /** Base URL for the login destination (portal URL, or request origin as fallback). */
  portalUrl: string;
  /** The pathname (including search) that the user was attempting to reach. */
  pathname: string;
  /** Login path on the destination origin. Defaults to "/login". */
  loginPath?: string;
  /**
   * Optional app basePath that should be stripped from `pathname` before it is
   * round-tripped back through the login flow. For ReportingOS this is
   * "/reporting". Leading slash required; trailing slashes are ignored.
   */
  basePath?: string;
  /**
   * Origin of the app initiating the redirect. When provided AND it differs
   * from the destination portalUrl origin, the `next` query parameter is
   * stamped as a FULL absolute URL (origin + unprefixed path) so the portal
   * can route the user back to the originating app after sign-in. When omitted
   * or matching the portal origin, the unprefixed relative path is stamped.
   */
  requestOrigin?: string;
}

/**
 * Build a full login redirect URL with the `next` query parameter set to
 * either the unprefixed relative pathname (same-origin) or the full absolute
 * URL (cross-origin) the user was attempting to reach.
 *
 * The query parameter is named `next` to match Agency OS's login page
 * convention (`apps/agency-os/src/app/login/page.tsx` reads `?next=`).
 */
export function buildLoginRedirect({
  portalUrl,
  pathname,
  loginPath = '/login',
  basePath,
  requestOrigin,
}: BuildLoginRedirectInput): string {
  const unprefixed = basePath ? stripBasePath(pathname, basePath) : pathname;
  const loginUrl = new URL(loginPath, portalUrl);
  const safeUnprefixed = unprefixed || '/';

  let nextValue: string = safeUnprefixed;
  if (requestOrigin) {
    const portalOrigin = safeOriginOf(portalUrl);
    const normalizedRequestOrigin = normalizeHttpOrigin(requestOrigin);
    if (
      portalOrigin &&
      normalizedRequestOrigin &&
      portalOrigin !== normalizedRequestOrigin
    ) {
      // Cross-origin: stamp absolute URL so the destination portal can route
      // the user back to the originating app's origin after sign-in.
      nextValue = `${normalizedRequestOrigin}${safeUnprefixed}`;
    }
  }

  loginUrl.searchParams.set('next', nextValue);
  return loginUrl.toString();
}

function safeOriginOf(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

export interface ResolvePublicOriginInput {
  /** Full request URL as seen by Next.js. */
  requestUrl: string;
  /** Request headers, used to honor proxy-provided public host metadata. */
  headers?: HeadersLike;
  /** Explicit public origin or app URL from a trusted app env var. */
  explicitOrigin?: string | null;
}

/**
 * Resolve the browser-reachable origin for auth redirects.
 *
 * Standalone Next.js apps may see an internal bind URL such as
 * `https://0.0.0.0:8080` in `request.url` behind a proxy. Redirecting users to
 * that origin breaks production auth. Prefer an explicit app URL, then
 * forwarded proxy headers, then the request URL as a last resort.
 *
 * SECURITY: this function does NOT validate the resolved host against any
 * allowlist -- it normalizes whatever `x-savage-public-host` /
 * `x-forwarded-host` / `host` value is present, which is untrusted input
 * whenever a caller can be reached directly (bypassing savage-portal-router,
 * e.g. via a service's raw `*.up.railway.app` domain). Do NOT use this
 * result directly as a redirect target for an unauthenticated request --
 * use {@link resolveTrustedPublicOrigin} instead, which gates the result
 * through {@link CANONICAL_PORTAL_HOSTS}.
 */
export function resolvePublicOrigin({
  requestUrl,
  headers,
  explicitOrigin,
}: ResolvePublicOriginInput): string {
  const explicit = normalizeHttpOrigin(explicitOrigin);
  if (explicit) return explicit;

  const fallbackProtocol = protocolFromRequestUrl(requestUrl);
  const savagePublicHost = firstHeaderValue(headers?.get('x-savage-public-host'));
  const savagePublicProto =
    firstHeaderValue(headers?.get('x-savage-public-proto')) ?? fallbackProtocol;
  const savagePublicOrigin = originFromHost(savagePublicHost, savagePublicProto);
  if (savagePublicOrigin) return savagePublicOrigin;

  const forwardedHost = firstHeaderValue(headers?.get('x-forwarded-host'));
  const forwardedProto =
    firstHeaderValue(headers?.get('x-forwarded-proto')) ?? fallbackProtocol;
  const forwardedOrigin = originFromHost(forwardedHost, forwardedProto);
  if (forwardedOrigin) return forwardedOrigin;

  const host = firstHeaderValue(headers?.get('host'));
  const hostOrigin = originFromHost(host, forwardedProto);
  if (hostOrigin) return hostOrigin;

  return new URL(requestUrl).origin;
}

/**
 * Canonical SAVAGE app hosts that savage-portal-router forwards traffic
 * for (see `platform/cloudflare/workers/savage-portal-router/worker.js`).
 * Single source of truth for "is this a trusted portal host" -- consumers
 * that build a redirect target from forwarded-host headers MUST gate
 * through this (or an app-specific equivalent, e.g. AgencyOS's
 * `isTrustedProductionHost`) before trusting the result.
 */
export const CANONICAL_PORTAL_HOSTS: ReadonlySet<string> = new Set([
  'portal.savagestrategic.io',
  'app.savagestrategic.io',
  'revisions.savagestrategic.io',
  'beta.savagestrategic.io',
  'staging.savagestrategic.io',
  'staging.portal.savagestrategic.io',
]);

export interface ResolveTrustedPublicOriginInput extends ResolvePublicOriginInput {
  /** Origin to use when the resolved origin's host is not in `trustedHosts`. */
  fallbackOrigin: string;
  /** Defaults to {@link CANONICAL_PORTAL_HOSTS}. */
  trustedHosts?: ReadonlySet<string>;
}

/**
 * Like {@link resolvePublicOrigin}, but only trusts a forwarded-host-derived
 * origin when its hostname is in `trustedHosts` -- otherwise returns
 * `fallbackOrigin`. Use this (not `resolvePublicOrigin` directly) whenever
 * the result becomes a redirect target for an unauthenticated request, so a
 * spoofed `X-Forwarded-Host` (reachable when a service's raw origin is hit
 * directly, bypassing savage-portal-router) can never redirect a user
 * off-platform.
 */
export function resolveTrustedPublicOrigin({
  requestUrl,
  headers,
  explicitOrigin,
  fallbackOrigin,
  trustedHosts = CANONICAL_PORTAL_HOSTS,
}: ResolveTrustedPublicOriginInput): string {
  const resolved = resolvePublicOrigin({ requestUrl, headers, explicitOrigin });
  let hostname: string | null;
  try {
    hostname = new URL(resolved).hostname.toLowerCase();
  } catch {
    hostname = null;
  }
  return hostname && trustedHosts.has(hostname) ? resolved : fallbackOrigin;
}

/**
 * Keep auth callback destinations on the current app origin. Supabase callback
 * URLs are user-visible, so never trust an absolute or protocol-relative
 * `next` value from the query string.
 */
export function safeRelativePath(
  value: string | null | undefined,
  fallback = '/dashboard'
): string {
  const raw = value?.trim();
  if (
    !raw ||
    // URL parsing removes tabs/newlines before resolving an origin. Check the
    // original value, before trimming, so a control cannot conceal "//".
    /[\u0000-\u001f\u007f]/.test(value ?? '') ||
    !raw.startsWith('/') ||
    raw.startsWith('//') ||
    raw.startsWith('/\\')
  ) {
    return fallback;
  }
  if (/^[a-z][a-z\d+\-.]*:/i.test(raw)) {
    return fallback;
  }
  return raw;
}

/**
 * Remove a basePath prefix from a pathname. Preserves trailing path + search
 * (caller is expected to pass pathname + search concatenated).
 */
export function stripBasePath(pathname: string, basePath: string): string {
  const normalized = basePath.replace(/\/+$/, '');
  if (!normalized) return pathname;
  const pattern = new RegExp(`^${escapeRegExp(normalized)}(?=\\/|$)`);
  const stripped = pathname.replace(pattern, '');
  return stripped.length > 0 ? stripped : '/';
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function firstHeaderValue(value: string | null | undefined): string | null {
  const first = value?.split(',')[0]?.trim();
  return first || null;
}

function protocolFromRequestUrl(requestUrl: string): 'http' | 'https' {
  try {
    const protocol = new URL(requestUrl).protocol.replace(':', '').toLowerCase();
    return protocol === 'http' ? 'http' : 'https';
  } catch {
    return 'https';
  }
}

function originFromHost(
  host: string | null,
  protocol: string | null
): string | null {
  if (!host || host.startsWith('/')) return null;
  const normalizedProtocol = protocol === 'http' ? 'http' : 'https';
  return normalizeHttpOrigin(`${normalizedProtocol}://${host}`);
}

function normalizeHttpOrigin(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;

  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.origin;
  } catch {
    return null;
  }
}
